import React from 'react';
import { cn } from '@/lib/utils';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import type { RiqueMessage, RiqueBriefing } from '@/lib/riqueApi';
import { RiqueRichContent } from '@/components/Rique/RiqueRichContent';
import { RiqueMarkdown } from '@/components/Rique/RiqueMarkdown';

interface RiqueMessageListProps {
  messages: RiqueMessage[];
  streamingContent?: string;
  isLoading?: boolean;
  progressLabel?: string | null;
  onStop?: () => void;
  compact?: boolean;
  expanded?: boolean;
  briefing?: RiqueBriefing | null;
  /** Empty state: short briefing only (chips live in composer) */
  emptyMinimal?: boolean;
  suggestions?: string[];
  onSuggestion?: (text: string) => void;
}

function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

function formatScopeChip(scope: { companyName?: string; dataFrom?: string; dataTo?: string }): string {
  const company = scope.companyName?.trim() || 'Todas';
  if (scope.dataFrom && scope.dataTo) {
    return `${company} · ${formatIsoDate(scope.dataFrom)} – ${formatIsoDate(scope.dataTo)}`;
  }
  return company;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const RiqueMessageList: React.FC<RiqueMessageListProps> = ({
  messages,
  streamingContent,
  isLoading,
  progressLabel,
  onStop,
  compact = false,
  expanded = false,
  briefing,
  emptyMinimal = false,
  suggestions = [],
  onSuggestion,
}) => {
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const stickToBottom = React.useRef(true);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottom.current = gap < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    if (!stickToBottom.current) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, isLoading, progressLabel]);

  const isEmpty = messages.length === 0 && !streamingContent && !isLoading;

  return (
    <div
      ref={scrollRef}
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-y-auto scroll-smooth',
        compact ? 'px-3 py-3' : 'px-4 py-6 sm:px-6'
      )}
    >
      {isEmpty ? (
        <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center gap-5 py-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white p-1.5 shadow-md ring-1 ring-border/50">
            <RiqueIcon className="h-full w-full" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              Olá! Sou o Rique.Ia
            </h1>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
              {emptyMinimal
                ? 'Pergunte sobre cobrança, frota, faturamento ou operação. Escolha um atalho ou digite abaixo.'
                : 'Analista do ecossistema PortalFly e Zeh Motoca.'}
            </p>
          </div>
          {suggestions.length > 0 && onSuggestion ? (
            <div className="grid w-full gap-2 sm:grid-cols-2">
              {suggestions.slice(0, 4).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onSuggestion(s)}
                  className="rounded-2xl border border-border/80 bg-card px-4 py-3 text-left text-sm text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
                >
                  {s}
                </button>
              ))}
            </div>
          ) : null}
          {briefing && briefing.sections.length > 0 ? (
            <div className="w-full rounded-2xl border border-border/70 bg-muted/25 p-4 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Briefing</p>
              {briefing.sections.slice(0, emptyMinimal ? 1 : undefined).map((section) => (
                <div key={section.titulo} className="mt-2">
                  <p className="text-xs font-medium text-foreground">{section.titulo}</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {section.itens.slice(0, emptyMinimal ? 3 : undefined).map((item) => (
                      <li key={item}>· {item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={cn('mx-auto flex w-full flex-col', expanded ? 'max-w-5xl gap-6' : 'max-w-full gap-4')}>
        {messages.map((msg) => {
          const isUser = msg.role === 'USER';
          return (
            <div
              key={msg.id}
              className={cn('flex gap-3', isUser ? 'justify-end' : 'justify-start')}
            >
              {!isUser ? (
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-border/40">
                  <RiqueIcon className="h-full w-full" />
                </div>
              ) : null}
              <div className={cn('min-w-0', isUser ? 'max-w-[85%] sm:max-w-[75%]' : 'flex-1')}>
                <div
                  className={cn(
                    'text-[15px] leading-relaxed',
                    isUser
                      ? 'whitespace-pre-wrap rounded-3xl rounded-br-lg bg-primary px-4 py-2.5 text-primary-foreground'
                      : 'text-foreground'
                  )}
                >
                  {isUser ? msg.content : <RiqueMarkdown content={msg.content} />}
                </div>
                {!isUser && msg.metadata?.scope ? (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">{formatScopeChip(msg.metadata.scope)}</p>
                ) : null}
                {!isUser && msg.metadata?.faqFastPath ? (
                  <p className="mt-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">Resposta rápida</p>
                ) : null}
                {!isUser && msg.metadata ? (
                  <div className="mt-3">
                    <RiqueRichContent
                      compact={compact}
                      expanded={expanded}
                      visualizations={msg.metadata.visualizations}
                      deepLinks={msg.metadata.deepLinks}
                      actionDraft={msg.metadata.actionDraft}
                    />
                  </div>
                ) : null}
                {msg.createdAt && !msg.id.startsWith('temp-') ? (
                  <p className={cn('mt-1 text-[10px] text-muted-foreground/70', isUser && 'text-right')}>
                    {formatTime(msg.createdAt)}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}

        {streamingContent ? (
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-border/40">
              <RiqueIcon className="h-full w-full" />
            </div>
            <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-foreground">
              <RiqueMarkdown content={streamingContent} />
              <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-foreground/70 align-middle" aria-hidden />
              {onStop ? (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={onStop}
                    className="rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/15"
                  >
                    Cancelar
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {isLoading && !streamingContent ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-border/40">
              <RiqueIcon className="h-full w-full" />
            </div>
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:0ms]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:300ms]" />
              </span>
              {progressLabel?.trim() || 'Consultando dados…'}
              {onStop ? (
                <button
                  type="button"
                  onClick={onStop}
                  className="rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive hover:bg-destructive/15"
                >
                  Cancelar
                </button>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
      <div ref={bottomRef} className="h-px shrink-0" />
    </div>
  );
};
